<template>
  <div class="cognito-callback-container">
    <LoadingIndicator :showError="errorMessage.length > 0">
      <div class="cognito-callback-container__error">
        <p class="error-message">{{ errorMessage }}</p>
        <Button @click="retryLogin" themeColor="primary" class="retry-button">
          Try Again
        </Button>
      </div>
    </LoadingIndicator>
  </div>
</template>

<script setup lang="ts">
import { Pages } from "@/pages/pages";
import cognitoAuth from "@/shared/access/CognitoAuth";
import { onMounted, ref } from "vue";
import LoadingIndicator from "./LoadingIndicator.vue";
import { Button } from "@progress/kendo-vue-buttons";

const isProcessing = ref(false);
const errorMessage = ref("");

const handleCallback = async function() {
    try {
        isProcessing.value = true;
        errorMessage.value = "";

        // Parse URL parameters
        const urlParams = new URLSearchParams(window.location.search);
        const code = urlParams.get("code");
        const errorValue = urlParams.get("error");
        const errorDescription = urlParams.get("error_description");

        if (errorValue) {
            console.error(errorDescription || errorValue);
            errorMessage.value = errorDescription || "Authentication failed. Please try again.";
            return;
        }

        if (!code) {
          retryLogin();
          return;
        }

        // Handle the login redirect
        const authState = await cognitoAuth.handleLoginRedirect();

        if (authState.value?.isAuthenticated) {
            // Get the original URI or default to home page
            const originalUri = cognitoAuth.getOriginalUri() || Pages.Login;
            cognitoAuth.removeOriginalUri();
            window.location.href = originalUri;
        } else {
            throw new Error("Authentication failed - invalid auth state");
        }
    } catch (error: any) {
        console.error("Cognito callback error:", error);
        errorMessage.value = error.message || "Authentication failed. Please try again.";
    } finally {
        isProcessing.value = false;
    }
}

const retryLogin = function() {
    // Clear any stored state and redirect to login
    cognitoAuth.removeOriginalUri();
    window.location.href = Pages.Login;
};


onMounted(() => {
    handleCallback();
});
</script>

<style scoped lang="scss">
.cognito-callback-container {
  min-height: 100vh;
  display: flex;
  align-items: center;
  justify-content: center;
  background: #f5f5f5;

  &__error {
    text-align: center;
    max-width: 400px;
    color: #dc3545;

    .error-message {
      color: #dc3545;
      background: #f8d7da;
      border: 1px solid #f5c6cb;
      border-radius: 4px;
      padding: 10px;
    }
  }

  .retry-button {
    margin-top: 20px;
  }
}
</style>